import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, X, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const CartPage = () => {
  const { cart, updateCartItem, removeFromCart, cartTotal, cartCount } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=checkout');
    } else {
      navigate('/checkout');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <ShoppingBag size={64} className="text-foreground/20 mx-auto mb-6" />
          <h1 className="font-heading text-3xl mb-4">Votre panier est vide</h1>
          <p className="text-foreground/60 mb-8">Découvrez notre collection et trouvez votre bonheur</p>
          <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
            Voir la boutique
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="cart-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="py-12 border-b border-black/10 mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-6xl tracking-tight"
          >
            Panier
          </motion.h1>
          <p className="text-foreground/60 mt-4">{cartCount} article{cartCount !== 1 ? 's' : ''}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.map((item, index) => (
              <motion.div
                key={`${item.product_id}-${item.size}-${item.color}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 pb-6 border-b border-black/10"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <Link to={`/product/${item.product_id}`} className="w-24 h-32 md:w-32 md:h-40 bg-black/10 flex-shrink-0">
                  <img
                    src={item.product?.images?.[0] || ''}
                    alt={item.product?.name || ''}
                    className="w-full h-full object-cover"
                  />
                </Link>

                {/* Details */}
                <div className="flex-1">
                  <div className="flex justify-between">
                    <div>
                      <Link to={`/product/${item.product_id}`}>
                        <h3 className="font-heading text-lg hover:text-gold transition-colors">
                          {item.product?.name}
                        </h3>
                      </Link>
                      <p className="text-foreground/50 text-sm mt-1">
                        {item.size} • {item.color}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id, item.size, item.color)}
                      className="p-1 hover:text-gold transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="flex items-end justify-between mt-4">
                    {/* Quantity */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateCartItem(item.product_id, item.quantity - 1, item.size, item.color)}
                        className="p-1 border border-black/20 hover:border-gold transition-colors"
                        data-testid={`quantity-minus-${item.product_id}`}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-accent">{item.quantity}</span>
                      <button
                        onClick={() => updateCartItem(item.product_id, item.quantity + 1, item.size, item.color)}
                        className="p-1 border border-black/20 hover:border-gold transition-colors"
                        data-testid={`quantity-plus-${item.product_id}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    {/* Price */}
                    <p className="price-tag text-gold text-lg">
                      {((item.product?.price || 0) * item.quantity).toFixed(2)} €
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-6 md:p-8 sticky top-28 border border-black/10"
              data-testid="order-summary"
            >
              <h2 className="font-heading text-2xl mb-6">Récapitulatif</h2>

              <div className="space-y-4 pb-6 border-b border-black/10">
                <div className="flex justify-between text-foreground/60">
                  <span>Sous-total</span>
                  <span>{cartTotal.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-foreground/60">
                  <span>Livraison</span>
                  <span>{cartTotal >= 150 ? 'Gratuite' : '9.90 €'}</span>
                </div>
              </div>

              <div className="flex justify-between mt-6 mb-8">
                <span className="font-heading text-lg">Total</span>
                <span className="price-tag text-gold text-xl" data-testid="cart-total">
                  {(cartTotal + (cartTotal >= 150 ? 0 : 9.90)).toFixed(2)} €
                </span>
              </div>

              {cartTotal < 150 && (
                <p className="text-sm text-foreground/50 mb-6">
                  Plus que {(150 - cartTotal).toFixed(2)} € pour la livraison gratuite
                </p>
              )}

              <button
                onClick={handleCheckout}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="checkout-btn"
              >
                Commander
                <ArrowRight size={16} />
              </button>

              <Link 
                to="/shop" 
                className="block text-center mt-4 text-sm text-foreground/60 hover:text-gold transition-colors"
              >
                Continuer mes achats
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
